import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Table, Modal, ModalHeader, ModalBody, ModalFooter, Input, FormGroup, Label, Badge } from 'reactstrap';
import { showErrorAlert, showSuccessAlert, showLoadingAlert } from "../../../components/custom/sweetalertHelper";
import showConfirmationAlert from '../../../components/custom/confirmationAlert';
import { addClientLicense } from '../../../slices/veloxity/client/ClientAddLicenseKey'; 
import { removeClientLicense } from '../../../slices/veloxity/client/ClientRemoveLicenseKey'; 

const CompanyLicenseKey = ({ licenses: initialLicenses, clientId: clientId }) => {  
    const [licenses, setLicenses] = useState(initialLicenses);  
    const [newLicense, setNewLicense] = useState({  type: '', license: '', name: '', primary: false });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');

    const dispatch = useDispatch(); 
    const { loading, error } = useSelector((state) => state.addClientLicense); 
    const { removeLoading, removeError } = useSelector((state) => state.removeClientLicense); 


    const toggleModal = () => {
        if (isModalOpen) {
            setNewLicense({  type: '', license: '', name: '', primary: false });
        }
        setIsModalOpen(!isModalOpen);
    };

    const handleAddLicense = () => {
        if (!newLicense.type || !newLicense.license || !newLicense.name) {
          showErrorAlert("All fields are required!");
          return;
        }
    
        showLoadingAlert('Adding License', 'Please wait...');
        dispatch(addClientLicense({ clientId, licenseData: newLicense }))
          .unwrap()
          .then(() => {
            setLicenses([...licenses, { ...newLicense, id: licenses.length + 1 }]);
            toggleModal();
            showSuccessAlert("License added successfully!");
          })
          .catch((err) => {
            showErrorAlert(err.detail[0].defaultMessage || err.message || 'Error');
          });
      };

      const handleDeleteLicense = (id, name) => {
        showConfirmationAlert(`Are you sure you want to delete ${name}?`, () => {
            showLoadingAlert('Deleting License', 'Please wait...');
            dispatch(removeClientLicense({ licenseKeyId: id })) 
                .unwrap()
                .then(() => {
                    setLicenses(licenses.filter((license) => license.id !== id));
                    showSuccessAlert("License deleted successfully!");
                })
                .catch((err) => {
                    showErrorAlert(err.detail[0].defaultMessage || err.message || 'Error deleting license');
                });
        });
    };

    // Input değerlerini güncelleme
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === "checkbox") {
            setNewLicense({ ...newLicense, [name]: checked });
        } else {
            setNewLicense({ ...newLicense, [name]: value });
        }
    };

    // Sıralama fonksiyonu
    const handleSort = (field) => {
        const sortedLicenses = [...licenses].sort((a, b) => {
            const aField = a[field].toString().toLowerCase();
            const bField = b[field].toString().toLowerCase();
            if (sortOrder === 'asc') {
                return aField > bField ? 1 : -1;
            } else {
                return aField < bField ? 1 : -1;
            }
        });
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        setLicenses(sortedLicenses);
        setSortField(field);
    };

    return (
        <div>
            <Button color="success" onClick={toggleModal}>
                <i className="ri-add-line align-bottom me-1"></i> New License
            </Button>

            <div className="table-responsive table-card mt-3" style={{ paddingLeft: '1%', paddingRight: '1%' }}>
                <Table hover responsive striped className="mb-0">
                    <thead className="table-light">
                        <tr style={{ textAlign: 'center' }}>
                            <th className="sort" onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                                License Name
                            </th>
                            <th className="sort" onClick={() => handleSort('license')} style={{ cursor: 'pointer' }}>
                                License Key
                            </th>
                            <th className="sort" onClick={() => handleSort('type')} style={{ cursor: 'pointer' }}>
                                Type
                            </th>
                            <th className="sort" onClick={() => handleSort('primary')} style={{ cursor: 'pointer' }}>
                                Primary
                            </th>
                            <th style={{ width: '150px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {licenses.map((license) => (
                            <tr key={license.id} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                <td>{license.name}</td>
                                <td>{license.license}</td>
                                <td>
                                    <Badge className={`ms-2 bg-${license.type === 'IOS' ? 'primary' : 'success'}  align-items-center`}>
                                        <i className={license.type === 'IOS' ? 'ri-apple-line fs-17 lh-1 align-middle me-1' : 'ri-android-line fs-17 lh-1 align-middle me-1'} />
                                        <span style={{ verticalAlign: 'middle', position: 'relative', top: '2px' }}>{license.type}</span>
                                    </Badge>
                                </td>
                                <td>
                                    {license.primary ? <i className="ri-check-line fs-17 text-success"></i> : <i className="ri-close-line fs-17 text-danger"></i>}
                                </td>
                                <td>
                                    <i
                                        className="ri-delete-bin-line fs-17 lh-1 align-middle text-danger"
                                        onClick={() => handleDeleteLicense(license.id, license.name)}
                                        style={{ cursor: 'pointer' }}
                                    ></i>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {/* Modal for Adding License */}
            <Modal isOpen={isModalOpen} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>
                    Add New License
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="name">License Name</Label>
                        <Input
                            type="text"
                            name="name"
                            id="name"
                            value={newLicense.name}
                            onChange={handleInputChange}
                            placeholder="Enter license name"
                            required
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="license">License Key</Label>
                        <Input
                            type="text"
                            name="license"
                            id="license"
                            value={newLicense.license}
                            onChange={handleInputChange}
                            placeholder="Enter license key"
                            required
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="type">Os Type</Label>
                        <Input
                            type="select"
                            name="type"
                            id="type"
                            className="form-control"
                            value={newLicense.type}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Select Type</option>
                            <option value="IOS">IOS</option>
                            <option value="ANDROID">ANDROID</option>
                        </Input>
                    </FormGroup>

                    <FormGroup check>
                        <Label check>
                            <Input
                                type="checkbox"
                                name="primary"
                                checked={newLicense.primary}
                                onChange={handleInputChange}
                            />{' '}
                            Primary License
                        </Label>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleAddLicense}>
                        Add License
                    </Button>
                    <Button color="secondary" onClick={toggleModal}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default CompanyLicenseKey;
